import React, { useEffect, useState } from "react";
import SensorTable from "./SensorTable";
import { Autocomplete, Box, Checkbox, CircularProgress, Divider, FormControlLabel, LinearProgress, Paper, Stack, TextField, useTheme } from "@mui/material";
import Accordion from "@mui/material/Accordion";
import AccordionActions from "@mui/material/AccordionActions";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Button from "@mui/material/Button";
import SearchIcon from "@mui/icons-material/Search";
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormHelperText from '@mui/material/FormHelperText';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';


// const measurementNameListHardCoded = [
//   "Level",
//   "Temperature",
//   "Free Volume",
//   "Fill Level",
//   "Used Volume",
//   "Available Volume",
//   "Mass To Fill",
//   "Mass Left",
//   "Leakage Percentage",
//   "Lost Steam",
//   "T Inlet",
//   "T Outlet",
//   "Lost Euros Per Day"
// ]


const SensorTableList = ({ allSensorSites }) => {
  const theme = useTheme();
  const [filter, setFilter] = useState("");
  const [filteredSensorSites, setFilteredSensorSites] = useState(allSensorSites);
  const [showSensorId, setShowSensorId] = useState(false);
  const [showTimestamp, setShowTimestamp] = useState(false);
  const [artificialLoadingProgress, setArtificialLoadingProgress] = useState(0);

  const [sensorValueFilterMeasurement, setSensorValueFilterMeasurement] = useState("");
  const [sensorValueFilterOperator, setSensorValueFilterOperator] = useState("");
  const [sensorValueFilterValue, setSensorValueFilterValue] = useState("");

  const [measurementNameList, setMeasurementNameList] = useState([]);


  const handleChangeMeasurementList = (newMeasurementList) => {
    setMeasurementNameList(newMeasurementList);
  }


  //artificial timer, this is not real loading progress and is there to indicate the delay(3s) that the socket connections have
  useEffect(() => {
    // 3 seconds loading indicator
    const timer = setInterval(() => {
      setArtificialLoadingProgress((prev) => {
        if (prev >= 100) {
          clearInterval(timer);
          return 100;
        }
        return prev + 8; // Increase progress by 8% every 300ms
      });
    }, 300);

    return () => {
      clearInterval(timer);
    };
  }, []);

  // filter customerName and site's device names using a simple input field
  useEffect(() => {
    if (filter) {
      const loweredFilter = filter.toLowerCase();
      const filteredSites = allSensorSites
        .map((site) => {
          const matchesCustomer = site.customerName.toLowerCase().includes(loweredFilter);

          // Filter devices by name match
          const matchingDevices = site.devices.filter((device) =>
            device.deviceName.toLowerCase().includes(loweredFilter)
          );

          if (matchesCustomer) {
            // If site name matches, include full site with all devices
            return site;
          } else if (matchingDevices.length > 0) {
            // If only some devices match, return site with just those
            return {
              ...site,
              devices: matchingDevices,
            };
          } else {
            // Site doesn't match and no devices match, exclude it
            return null;
          }
        })
        .filter((site) => site !== null);

      setFilteredSensorSites(filteredSites);
    } else {
      // If no filter, reset to all sensor sites
      setFilteredSensorSites(allSensorSites);
    }

  }, [filter, allSensorSites]);

  return (
    <Box sx={{ padding: 2 }}>
      <Stack direction={{ sm: "column", md: "row" }} spacing={2} sx={{ marginBottom: 1, alignItems: "center" }}>
        <Box sx={{ display: "flex", alignItems: "flex-end", alignItems: "center" }}>
          <SearchIcon sx={{ mr: 1 }} />
          <TextField
            label="Search Site or System"
            variant="outlined"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </Box>
        <Box sx={{ display: "flex", justifyContent: "center", flexDirection: "column" }}>
          <Typography sx={{ fontSize: "14px" }}>Show Sensor IDs</Typography>
          <Checkbox checked={showSensorId} onChange={(e) => setShowSensorId(e.target.checked)} color="primary" />
        </Box>
        <Box sx={{ display: "flex", justifyContent: "center", flexDirection: "column" }}>
          <Typography sx={{ fontSize: "14px" }}>Show Timestamps</Typography>
          <Checkbox checked={showTimestamp} onChange={(e) => setShowTimestamp(e.target.checked)} color="primary" />
        </Box>
        <Divider orientation="vertical" flexItem />
        <Box sx={{ display: "flex", justifyContent: "center", flexDirection: { xs: "column", sm: "row" }, alignItems: { xs: "flex-start", sm: "center" }, gap: 1 }}>
          <Typography sx={{ fontSize: "14px" }}>Highlight Cells by Value</Typography>
          <Autocomplete
            size="small"
            options={measurementNameList}
            getOptionLabel={(option) => option.label}
            value={measurementNameList.find((m) => m.value === sensorValueFilterMeasurement) || null}
            onChange={(event, newValue) => {
              setSensorValueFilterMeasurement(newValue ? newValue.value : "");
            }}
            renderInput={(params) => <TextField {...params} label="Measurement" />}
            sx={{ minWidth: 210 }}
          />
          <FormControl sx={{ minWidth: 140 }} size="small">
            <InputLabel id="select-operator-to-filter-label">Operator</InputLabel>
            <Select
              labelId="select-operator-to-filter-label"
              id="select-operator-to-filter"
              value={sensorValueFilterOperator}
              label="Operator"
              onChange={(e) => setSensorValueFilterOperator(e.target.value)}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              <MenuItem value="greaterThan">Greater Than</MenuItem>
              <MenuItem value="lessThan">Less Than</MenuItem>
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="Value"
            variant="outlined"
            value={sensorValueFilterValue}
            onChange={(e) => setSensorValueFilterValue(e.target.value)}
            sx={{ maxWidth: 150 }}
          />
        </Box>
      </Stack>
      {artificialLoadingProgress < 100 && (
        <LinearProgress variant="determinate" value={artificialLoadingProgress} sx={{ my: 2, mx: 1 }} />
      )}
      {
        allSensorSites.length > 0 ? (
          allSensorSites.map((site, index) => {
            const loweredFilter = filter.toLowerCase();
            const matchesCustomer = site.customerName.toLowerCase().includes(loweredFilter);
            const matchingDevices = site.devices.filter((device) =>
              device.deviceName.toLowerCase().includes(loweredFilter)
            );

            const siteMatches = matchesCustomer || matchingDevices.length > 0;

            return (
              <Accordion
                defaultExpanded
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  backgroundColor: theme.palette.background.paper,
                  display: siteMatches ? "block" : "none", // hide unfiltered sites
                }}
                key={index}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={site.customerName + "-content"}
                  id={site.customerName + "-header"}
                >
                  <Typography variant="h5" color="primary">
                    {site.customerName}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {site.devices.map((system, sysIndex) => {
                    const deviceMatches = system.deviceName.toLowerCase().includes(loweredFilter);
                    const showDevice = matchesCustomer || deviceMatches;

                    return (
                      <Paper
                        key={sysIndex}
                        sx={{
                          marginBottom: 2,
                          padding: 1,
                          display: showDevice ? "block" : "none", // hide unfiltered devices
                        }}
                        elevation={6}
                      >
                        <Typography variant="h6" sx={{ marginLeft: 1 }}>
                          {system.deviceName}
                        </Typography>
                        <Divider />
                        <SensorTable
                          system={system}
                          site={site}
                          showSensorId={showSensorId}
                          showTimestamp={showTimestamp}
                          sensorValueFilterMeasurement={sensorValueFilterMeasurement}
                          sensorValueFilterOperator={sensorValueFilterOperator}
                          sensorValueFilterValue={sensorValueFilterValue}
                          handleChangeMeasurementList={handleChangeMeasurementList}
                          measurementNameList={measurementNameList}
                        />
                      </Paper>
                    );
                  })}
                </AccordionDetails>
              </Accordion>
            );
          })
        ) : (
          <Typography variant="body1" color="textSecondary">
            No sensor sites available.
          </Typography>
        )}
    </Box>
  );
}

export default SensorTableList;